import { ViewerDocumentKind, ViewerDownloadTargetKind, type ContentViewerSession, type ViewerDownloadTarget } from '../store/schemas'
import { store } from '@/store/store'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { saveHTMLasDocx } from '@/chat-common/fetch/post-download-html-docx'
import { sanitizeProviderHtml } from './sanitize-and-highlight-html'
import { formatHtmlForDocXTransformation } from '@/chat-common/util/download-conversation'
import { getCaselawReferenceHtml } from '../fetch/get-caselaw-reference-html'
import { getLrrReferenceHtml } from '../fetch/get-lrr-reference-html'
import { downloadFile as downloadUserFilesPipelineFile } from '@/user_files/files/fetch/download-user-file'
import { resolveUserFile } from '@/user_files/files/utils/resolve-user-files'
import { saveAs } from 'file-saver'
import { fetchAgencyDocument, fetchAgencyOriginal } from '@/agent-v2/agency-documents/fetch/agency-document'
import { getAgencyOriginalFormat } from '@/agent-v2/agency-documents/utils/agency-original-format'
import { formatCaselawExportHtml } from '@/citator/utils/format-caselaw-export-html'

/**
 * Handles downloading the active content viewer session.
 * @param contentViewerContent
 * @returns
 */
export default async function handleContentViewerDownload(contentViewerContent: ContentViewerSession | null) {
  if (!contentViewerContent) return

  try {
    await downloadBySession(contentViewerContent)
  } catch (error) {
    console.error('Content viewer download failed', error)
    store.dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'File download failed.', durationMs: kToastDurationMediumMs }))
  }
}

async function downloadBySession(session: ContentViewerSession) {
  const downloadTarget = session.document.downloadTarget
  if (!downloadTarget) return

  switch (downloadTarget.kind) {
    case ViewerDownloadTargetKind.AGENCY_ORIGINAL: {
      // Same gate as the viewer: only a saved PDF, plain-text, or Markdown original can be delivered.
      // Every other agency reference keeps its saved evidence and has no download, so say so instead of failing.
      const document = await fetchAgencyDocument(downloadTarget.conversationId, downloadTarget.referenceId, undefined, downloadTarget.tableId)
      if (!getAgencyOriginalFormat(document.media_type) || !document.original_available) {
        store.dispatch(
          openGlobalToast({ type: GlobalToastType.INFO, message: 'No downloadable original for this source.', durationMs: kToastDurationMediumMs })
        )
        break
      }
      const original = await fetchAgencyOriginal(downloadTarget.conversationId, downloadTarget.referenceId, undefined, false, downloadTarget.tableId)
      saveAs(original.blob, original.fileName || 'decision')
      break
    }
    case ViewerDownloadTargetKind.USER_FILE_ORIGINAL: {
      await downloadUserFile(downloadTarget.userFileId)
      break
    }

    case ViewerDownloadTargetKind.RICH_REFERENCE_DOCX: {
      const downloadedFileName = await downloadRichReferenceTargetAsDocX(
        downloadTarget,
        session.document.kind === ViewerDocumentKind.CASELAW ? session.document.caseStatus : undefined
      )
      if (downloadedFileName !== undefined) {
        store.dispatch(
          openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${downloadedFileName}`, durationMs: kToastDurationShortMs })
        )
      }
      break
    }
  }
}

// Resolve a user file by id and download its original upload. Shared by the citation and
// USER_FILE download paths, which both have an id but not the original path.
async function downloadUserFile(userFileId: string) {
  const userFile = await resolveUserFile(userFileId)
  if (!userFile) throw new Error(`Could not resolve user file ${userFileId}`)

  await downloadUserFilesPipelineFile({ fileId: userFile.id, storagePath: userFile.storage_path, fileName: userFile.original_filename })
  store.dispatch(
    openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Download complete: ${userFile.original_filename}`, durationMs: kToastDurationShortMs })
  )
}

async function downloadRichReferenceTargetAsDocX(
  target: Extract<ViewerDownloadTarget, { kind: ViewerDownloadTargetKind.RICH_REFERENCE_DOCX }>,
  caseStatus?: string | null
) {
  if (!target.storagePath) throw new Error('No s3_path found in reference metadata')
  const response =
    target.sourceKind === ViewerDocumentKind.CASELAW ? await getCaselawReferenceHtml(target.storagePath) : await getLrrReferenceHtml(target.storagePath)
  const htmlContent = response.data
  if (!htmlContent) {
    console.error('Error fetching reference HTML')
    return
  }

  const sanitizedHtml = sanitizeProviderHtml(htmlContent)
  const exportHtml = target.sourceKind === ViewerDocumentKind.CASELAW ? await formatCaselawExportHtml(sanitizedHtml, caseStatus) : sanitizedHtml
  const outputHtml = formatHtmlForDocXTransformation(exportHtml)
  return saveHTMLasDocx({ html: outputHtml, fileName: target.fileName })
}
